// Guide §10.2 — payload schemas for all 28 operation types. Shared by the phone (before writing the outbox) and the server (before applying).
import { z } from 'zod';
import { RETURN_REASONS } from '../enums';
import { MODULE_OP_SCHEMAS, type ModuleOpType } from '../modules/schemas';

import { zId, zMoney, zMoneyPos, zQty, zQtyPos, zFactor, zDate, zIso, zText, zMethod, zUnitName, zDiscount, zMediaUrl } from './primitives';
export * from './primitives';
// ── sales ─────────────────────────────────────────────────────────────────────
export const zSaleLine = z.object({
  id: zId,
  productId: zId.nullable().optional(),
  variantId: zId.nullable().optional(),
  batchId: zId.nullable().optional(),
  serialNo: zText(40).nullable().optional(),
  nameSnapshot: zText(200).min(1),
  unit: zText(20).default('pcs'),
  unitFactor: zFactor.default('1'),
  qty: zQtyPos,
  unitPrice: zMoneyPos,
  discount: zDiscount.nullable().optional(),
});

export const zSalePayment = z.object({
  id: zId,
  method: zMethod,
  amount: zMoneyPos,
  moneyAccountId: zId,
  reference: zText(60).nullable().optional(),
});

export const zSaleCreate = z.object({
  invoiceNo: z.string().regex(/^[A-Z]{1,4}-\d{6,}$/),
  deviceCode: z.string().regex(/^[A-Z]{1,4}$/),
  deviceSeq: z.number().int().positive(),
  customerId: zId.nullable().optional(),
  lines: z.array(zSaleLine).min(1).max(200),
  discount: zDiscount.nullable().optional(),
  promotionId: zId.nullable().optional(),
  payments: z.array(zSalePayment).max(8),
  dueDate: zDate.nullable().optional(),
  installments: z.array(z.object({ amount: zMoneyPos, dueDate: zDate })).max(60).optional(),
  rounding: z.boolean().default(false),
  /** v10: the shop's rounding step / direction at the time of the sale (absent = ৳1 nearest, as before) */
  roundingStep: z.union([z.literal(1), z.literal(5), z.literal(10)]).optional(),
  roundingMode: z.enum(['nearest', 'down']).optional(),
  linkToken: z.string().regex(/^[0-9A-Za-z]{22}$/),
  note: zText(300).nullable().optional(),
  replacesSaleId: zId.nullable().optional(),
  /** What the phone computed — the server recomputes and only flags a mismatch (Guide §11.1) */
  claimed: z.object({ total: zMoney, dueAmount: zMoney }),
  /** Agriculture: the crop season this sale belongs to (Dev Guide §7.2) */
  seasonId: zId.nullable().optional(),
  smsInvoice: z.boolean().default(true),
});

export const zSaleVoid = z.object({ reason: zText(200).min(2) });
export const zSaleRestore = z.object({});

export const zReturnVoid = z.object({ reason: zText(200).min(2) });

export const zReturnCreate = z.object({
  returnNo: z.string().regex(/^R-[A-Z]{1,4}-\d{6,}$/),
  saleId: zId,
  reason: z.enum(RETURN_REASONS),
  note: zText(300).nullable().optional(),
  lines: z.array(z.object({ id: zId, saleItemId: zId, qty: zQtyPos, restock: z.boolean() })).min(1).max(200),
  /** Where refunded cash comes from (required when refundCash > 0, enforced by the server after computing) */
  moneyAccountId: zId.nullable().optional(),
  /** Exchange: the new sale created in the same batch (op order guarantees it applies first) */
  exchangeSaleId: zId.nullable().optional(),
});

// ── baki (customer ledger) ───────────────────────────────────────────────────
export const zBakiCredit = z.object({
  customerId: zId,
  kind: z.enum(['opening', 'adjustment']),
  amount: zMoneyPos,
  dueDate: zDate.nullable().optional(),
  note: zText(200).nullable().optional(),
});
export const zBakiPayment = z.object({
  customerId: zId,
  amount: zMoneyPos,
  method: zMethod,
  moneyAccountId: zId,
  dueDate: zDate.nullable().optional(),
  reference: zText(60).nullable().optional(),
  saleId: zId.nullable().optional(),
  installmentId: zId.nullable().optional(),
  note: zText(200).nullable().optional(),
});
export const zBakiWriteoff = z.object({
  customerId: zId,
  amount: zMoneyPos,
  markDoubtful: z.boolean().default(false),
  note: zText(200).nullable().optional(),
});

// ── customers ────────────────────────────────────────────────────────────────
export const zCustomerUpsert = z.object({
  name: zText(120).min(1),
  phone: zText(20).nullable().optional(),
  address: zText(300).nullable().optional(),
  note: zText(300).nullable().optional(),
  tier: z.enum(['dealer', 'retailer', 'consumer']).nullable().optional(),
  creditLimit: zMoneyPos.nullable().optional(),
  isDoubtful: z.boolean().optional(),
  smsOptOut: z.boolean().optional(),
  /** On create only: opening baki */
  openingBalance: zMoneyPos.nullable().optional(),
  /** v10: the customer's photo (camera or gallery), uploaded first through POST /media */
  photoUrl: zMediaUrl.nullable().optional(),
});
export const zCustomerDelete = z.object({});

// ── products ─────────────────────────────────────────────────────────────────
export const zProductUnit = z.object({ id: zId, unitName: zText(30).min(1), factor: zFactor, sellPrice: zMoneyPos.nullable().optional(), barcode: zText(64).nullable().optional() });
export const zProductVariant = z.object({
  id: zId,
  attrs: z.record(z.string(), z.string().max(60)),
  barcode: zText(64).nullable().optional(),
  sellPrice: zMoneyPos.nullable().optional(),
  deleted: z.boolean().optional(),
});
export const zPriceTier = z.object({ id: zId, tier: z.enum(['dealer', 'retailer', 'consumer', 'bulk']), minQty: zQtyPos.nullable().optional(), price: zMoneyPos });

export const zProductUpsert = z.object({
  name: zText(200).min(1),
  baseUnit: zUnitName.default('pcs'),
  sellPrice: zMoneyPos,
  costPrice: zMoneyPos.nullable().optional(),
  trackStock: z.boolean().default(true),
  reorderLevel: zQtyPos.nullable().optional(),
  barcode: zText(64).nullable().optional(),
  tags: z.array(zText(40)).max(20).default([]),
  medicineId: zId.nullable().optional(),
  hasVariants: z.boolean().default(false),
  hasBatches: z.boolean().default(false),
  hasSerials: z.boolean().default(false),
  attrs: z.record(z.string(), z.unknown()).default({}),
  units: z.array(zProductUnit).max(10).optional(),
  variants: z.array(zProductVariant).max(200).optional(),
  tiers: z.array(zPriceTier).max(20).optional(),
  /** On create only */
  opening: z
    .object({ qty: zQtyPos, unitCost: zMoneyPos, batchNo: zText(40).nullable().optional(), expiryDate: zDate.nullable().optional(), variantId: zId.nullable().optional(), serialNos: z.array(zText(40)).max(500).optional() })
    .nullable()
    .optional(),
});
export const zProductDelete = z.object({});

// ── stock adjust ─────────────────────────────────────────────────────────────
export const zStockAdjust = z.object({
  productId: zId,
  variantId: zId.nullable().optional(),
  batchId: zId.nullable().optional(),
  mode: z.enum(['count', 'damage', 'expired']),
  /** count: the counted quantity; damage/expired: the quantity lost */
  qty: zQtyPos,
  note: zText(200).nullable().optional(),
});

// ── vendor & purchases ───────────────────────────────────────────────────────
export const zVendorUpsert = z.object({
  name: zText(120).min(1),
  phone: zText(20).nullable().optional(),
  address: zText(300).nullable().optional(),
  note: zText(300).nullable().optional(),
  openingPayable: zMoneyPos.nullable().optional(),
});
export const zPurchaseItem = z.object({
  id: zId,
  productId: zId,
  variantId: zId.nullable().optional(),
  batchNo: zText(40).nullable().optional(),
  expiryDate: zDate.nullable().optional(),
  serialNos: z.array(zText(40)).max(500).optional(),
  qty: zQtyPos,
  unit: zText(20).default('pcs'),
  unitFactor: zFactor.default('1'),
  unitCost: zMoneyPos,
});
export const zPurchaseCreate = z.object({
  vendorId: zId,
  billNo: zText(40).nullable().optional(),
  orderedAt: zIso.nullable().optional(),
  receivedAt: zIso,
  items: z.array(zPurchaseItem).min(1).max(200),
  discount: zMoneyPos.default('0'),
  paidAtPurchase: zMoneyPos.default('0'),
  moneyAccountId: zId.nullable().optional(),
  dueDate: zDate.nullable().optional(),
  note: zText(300).nullable().optional(),
});
export const zPurchaseVoid = z.object({ reason: zText(200).min(2) });
export const zVendorPayment = z.object({
  vendorId: zId,
  purchaseId: zId.nullable().optional(),
  amount: zMoneyPos,
  method: zMethod,
  moneyAccountId: zId,
  reference: zText(60).nullable().optional(),
});
export const zVendorReturn = z.object({
  vendorId: zId,
  purchaseId: zId.nullable().optional(),
  reason: zText(200).min(1),
  settlement: z.enum(['payable_adjust', 'cash_refund']),
  moneyAccountId: zId.nullable().optional(),
  items: z.array(z.object({ id: zId, productId: zId, batchId: zId.nullable().optional(), serialNo: zText(40).nullable().optional(), qty: zQtyPos, unitCost: zMoneyPos })).min(1).max(200),
});

// ── expenses & money ─────────────────────────────────────────────────────────
export const zExpenseCreate = z.object({
  categoryId: zId,
  amount: zMoneyPos,
  moneyAccountId: zId,
  note: zText(200).nullable().optional(),
  bizDate: zDate.optional(),
  /** Agriculture: input cost of a crop season */
  seasonId: zId.nullable().optional(),
});
export const zExpenseVoid = z.object({ reason: zText(200).optional() });
/** v10 — correct an expense in one step: entityId = the wrong expense (voided), `newId` = the corrected one (created) */
export const zExpenseEdit = zExpenseCreate.extend({ newId: zId, reason: zText(200).optional() });
export const zMoneyTransfer = z.object({ fromAccountId: zId, toAccountId: zId, amount: zMoneyPos, note: zText(200).nullable().optional() });
export const zMoneyAdjust = z.object({
  moneyAccountId: zId,
  /** + owner puts money in, − owner takes out, or adjustment after counting */
  amount: zMoney,
  reason: z.enum(['owner_in', 'owner_out', 'adjustment', 'opening']),
  note: zText(200).nullable().optional(),
});
export const zCashClose = z.object({ moneyAccountId: zId, bizDate: zDate, counted: zMoneyPos, note: zText(200).nullable().optional() });
export const zNoteUpsert = z.object({ text: zText(2000).min(1), remindAt: zIso.nullable().optional(), deleted: z.boolean().optional() });


// ── category modules (Guide §7.2, R1.0) ───────────────────────────────────────
export const zRepairUpsert = z.object({
  customerId: zId.nullable().optional(),
  customerName: zText(120).nullable().optional(),
  customerPhone: zText(20).nullable().optional(),
  deviceName: zText(120).min(1),
  problem: zText(300).min(1),
  status: z.enum(['received', 'in_progress', 'ready', 'delivered', 'cancelled']).default('received'),
  charge: zMoneyPos.default('0'),
  advance: zMoneyPos.default('0'),
  promisedAt: zIso.nullable().optional(),
  deliveredAt: zIso.nullable().optional(),
  saleId: zId.nullable().optional(),
  note: zText(300).nullable().optional(),
  deleted: z.boolean().optional(),
});
export const zServiceRateUpsert = z.object({
  name: zText(80).min(1),
  rate: zMoneyPos,
  unitLabel: zText(20).default('পৃষ্ঠা'),
  isActive: z.boolean().default(true),
  deleted: z.boolean().optional(),
});
export const zBundleUpsert = z.object({
  name: zText(120).min(1),
  className: zText(40).nullable().optional(),
  items: z.array(z.object({ productId: zId, qty: zQtyPos })).min(1).max(60),
  isActive: z.boolean().default(true),
  deleted: z.boolean().optional(),
});
export const zFieldDefUpsert = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
  label: zText(60).min(1),
  type: z.enum(['text', 'number', 'date', 'choice']),
  options: z.array(zText(40)).max(30).nullable().optional(),
  order: z.number().int().min(0).max(999).default(0),
  deleted: z.boolean().optional(),
});

// ── registry ─────────────────────────────────────────────────────────────────
const CORE_OP_SCHEMAS = {
  'sale.create': zSaleCreate,
  'sale.void': zSaleVoid,
  'sale.restore': zSaleRestore,
  'return.create': zReturnCreate,
  'return.void': zReturnVoid,
  'baki.credit': zBakiCredit,
  'baki.payment': zBakiPayment,
  'baki.writeoff': zBakiWriteoff,
  'customer.upsert': zCustomerUpsert,
  'customer.delete': zCustomerDelete,
  'product.upsert': zProductUpsert,
  'product.delete': zProductDelete,
  'stock.adjust': zStockAdjust,
  'purchase.create': zPurchaseCreate,
  'purchase.void': zPurchaseVoid,
  'vendor.upsert': zVendorUpsert,
  'vendor.payment': zVendorPayment,
  'vendor.return': zVendorReturn,
  'expense.create': zExpenseCreate,
  'expense.void': zExpenseVoid,
  'expense.edit': zExpenseEdit,
  'money.transfer': zMoneyTransfer,
  'money.adjust': zMoneyAdjust,
  'cash.close': zCashClose,
  'note.upsert': zNoteUpsert,
  'repair.upsert': zRepairUpsert,
  'service_rate.upsert': zServiceRateUpsert,
  'bundle.upsert': zBundleUpsert,
  'field_def.upsert': zFieldDefUpsert,
} as const;

export type CoreOpType = keyof typeof CORE_OP_SCHEMAS;
export type OpType = CoreOpType | ModuleOpType;
type ModuleSchemas = { [K in ModuleOpType]: z.ZodType<Record<string, unknown>, z.ZodTypeDef, Record<string, unknown>> };
export const OP_SCHEMAS = { ...CORE_OP_SCHEMAS, ...MODULE_OP_SCHEMAS } as typeof CORE_OP_SCHEMAS & ModuleSchemas;
export const OP_TYPES = Object.keys(OP_SCHEMAS) as OpType[];
export type OpPayload<T extends OpType> = z.infer<(typeof OP_SCHEMAS)[T]>;
export type OpPayloadInput<T extends OpType> = z.input<(typeof OP_SCHEMAS)[T]>;
